import { Accordion, AccordionDetails, Box, Button, makeStyles } from '@material-ui/core';
import { formatDateTimeReadable } from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import {
  ProgressStep,
  ProgressStepStatus,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/Progress/_shared/types';
import { AbstractProgress } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/Progress/AbstractProgress';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

import { AccordionProgressSummary } from './AccordionProgressSummary';
import { ApplicationStep } from './ApplicationStep';
import { StepStatusIcon } from './StepStatusIcon';

interface ApplicationStageProps {
  progress: AbstractProgress;
  stage: ProgressStep[];
}

export const ApplicationStage = ({ stage, progress }: ApplicationStageProps) => {
  const { palette } = useCustomTheme();

  const mainStep = stage.find((step) => step.isMainStep);

  const filteredStage = !progress.isActive
    ? stage.filter((step) => progress.getProgressStepDetails(step)?.status === ProgressStepStatus.COMPLETED)
    : stage;

  const stageHasNextSteps = filteredStage?.some((step) => !step.isMainStep && !step.isHidden);

  const [expanded, setExpanded] = useState<boolean>(
    progress.currentStep?.status === mainStep?.status && stageHasNextSteps,
  );

  const isCurrentStageNonActive = progress.isCurrentStepNonActive();

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  if (!mainStep) {
    return null;
  }

  const progressStepDetails = progress.getProgressStepDetails(mainStep);

  const opacityByStepStatus = (currentStep: ProgressStep) => {
    const stepDetails = progress.getProgressStepDetails(currentStep);
    return stepDetails?.status === ProgressStepStatus.FUTURE || stepDetails?.status === ProgressStepStatus.PENDING
      ? { opacity: 0.6 }
      : { opacity: 1 };
  };
  const classes = useStyles();

  return (
    <>
      <Accordion className={classes.root} variant={'elevation'} elevation={0} expanded={expanded}>
        <AccordionProgressSummary expanded={expanded} hideIcon>
          <Box
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'space-between'}
            style={opacityByStepStatus(mainStep)}
          >
            <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
              <StepStatusIcon stepStatus={progressStepDetails.status} />
              <Font
                variant={'body2'}
                color={'textSecondary'}
                style={
                  isCurrentStageNonActive
                    ? {
                        padding: '4px 8px',
                        borderRadius: '16px',
                      }
                    : {}
                }
              >
                <Text strong tId={stage.find((step) => step.isMainStep)?.nameTextId ?? ''} />
              </Font>
            </Box>
          </Box>
          <Box>
            <Font variant={'caption'} color={'textSecondary'}>
              {progressStepDetails?.updatedAt ? formatDateTimeReadable(progressStepDetails?.updatedAt) : undefined}
              {undefined}
            </Font>
          </Box>
        </AccordionProgressSummary>
        <AccordionDetails style={{ padding: '0px 8px 4px 8px' }}>
          <Box display={'flex'} flexDirection={'column'} width={'100%'}>
            {stageHasNextSteps ? (
              filteredStage.map(
                (step, i) =>
                  !step.isMainStep && (
                    <Box
                      display={'flex'}
                      flexDirection={'row'}
                      alignItems={'center'}
                      key={i}
                      style={opacityByStepStatus(step)}
                    >
                      <ApplicationStep key={i} progress={progress} step={step} />
                    </Box>
                  ),
              )
            ) : (
              <Box ml={1} mr={2} borderLeft={`1px solid ${palette.grey[200]}`} height={'12px'} />
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
      <Box display={'flex'} flexDirection={'row'} width={'100%'} height={'16px'} alignItems={'center'} flexGrow={1}>
        {stageHasNextSteps && (
          <>
            <Button
              className={classes.text}
              variant={'text'}
              onClick={() => toggleExpanded()}
              style={{ height: '18px', zIndex: 1, marginTop: '-6px' }}
              fullWidth
            >
              <Box
                borderBottom={`1px solid ${palette.grey[200]}`}
                width={'-webkit-fill-available'}
                style={{ opacity: 0.5 }}
              />
              <Box minWidth={'76px'} display={'flex'} justifyContent={'center'} px={'8px'} height={'auto'}>
                <Font variant={'caption'} color={'textSecondary'}>
                  {expanded ? (
                    <Box className={'fade-in-anim'}>
                      <Text tId={'SHOW_LESS'} />
                    </Box>
                  ) : (
                    <Text tId={'SHOW_MORE'} />
                  )}
                </Font>
              </Box>
              <Box
                borderBottom={`1px solid ${palette.grey[200]}`}
                width={'-webkit-fill-available'}
                style={{ opacity: 0.5 }}
              />
            </Button>
          </>
        )}
      </Box>
    </>
  );
};

const useStyles = makeStyles({
  root: {
    backgroundColor: 'transparent',
    border: 'none',
    minHeight: '28px',
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    margin: '0px 0px 0px 0px !important',
    '&:before': {
      display: 'none',
    },
    '&:hover': {
      backgroundColor: 'inherit',
      pointer: 'default',
    },
  },
  text: {
    '& .MuiButton-text': {
      margin: '0px 0px 0px 0px !important',
    },
  },
});
